import { lazy, Suspense } from 'react';
import debug from 'debug';
import { Router, Switch, Route, Redirect } from 'react-router-dom';

import MainNavigation from './components/Navigation/MainNavigation';
import HelmetHelper from 'components/Helmet/HelmetHelper';
import LoadingScreen from 'components/Progress/LoadingScreen';
import Footer from 'components/Navigation/Footer';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';
import ExternalRedirect from 'components/Helpers/ExternalRedirect';
import { history } from './index';
import * as Sentry from '@sentry/react';
import { IS_MAINTENANCE_MODE } from 'environment';
// import { IS_SNAP } from 'environment';
const IS_SNAP = false;
const SentryRoute = Sentry.withSentryRouting(Route);

// Ready
const MaintenancePageScreen = lazy(() => import('./screens/Page/MaintenancePage' /* webpackChunkName: "maintenance" */));
const HomeScreen = lazy(() => import('./screens/Home' /* webpackChunkName: "home" */));
const ProductDetailsScreen = lazy(() => import('./screens/Product/ProductDetailsScreen' /* webpackChunkName: "product" */));
const DynamicCollectionScreen = lazy(() => import('./screens/Collection/DynamicCollectionScreen' /* webpackChunkName: "collection" */));
const DefaultPageScreen = lazy(() => import('./screens/Page/DefaultPage' /* webpackChunkName: "cms" */));
const CartScreen = lazy(() => import('./screens/Cart/CartScreen' /* webpackChunkName: "cart" */));
const NotFound = lazy(() => import('./screens/NotFound/NotFound' /* webpackChunkName: "notfound" */));
// const CorporateCollectionScreen = lazy(() => import('./screens/Collection/CorporateCollectionScreen' /* webpackChunkName: "collection" */));
const RedemptionLandingScreen = lazy(() => import('./screens/Redeem/RedemptionLandingScreen' /* webpackChunkName: "redemptionLanding" */));
const RedemptionActivationScreen = lazy(() => import('./screens/Redeem/RedemptionActivationScreen' /* webpackChunkName: "redemptionActivation" */));
const RedemptionPostActivationScreen = lazy(() => import('./screens/Redeem/RedemptionPostActivationScreen' /* webpackChunkName: "redemptionPostActivation" */));
const ProductFinderScreen = lazy(() => import('./screens/Redeem/ProductFinderScreen' /* webpackChunkName: "redemptionFinder" */));
const FullResultSearchScreen = lazy(() => import('./screens/Page/FullSearchResultPage' /* webpackChunkName: "search" */));
const CardFinderScreen = lazy(() => import('./screens/Finder/CardFinderScreen' /* webpackChunkName: "cardFinder" */));
const AuditScreen = lazy(() => import('./screens/Audit/AuditScreen' /* webpackChunkName: "audit" */));
const d = debug('router');

const CGRouter = () => {
  d('route', IS_SNAP);

  // Setting up special routing for "snapped" pages... essentially we're trying to use a .html extension
  const productRedirectPath = !IS_SNAP ? '/product/:productHandle.html' : '/product/:productHandle';
  const productRoutePath = !IS_SNAP ? '/product/:productHandle' : '/product/:productHandle.html';

  const collectionRedirectPath = !IS_SNAP ? '/landing/:collectionHandle.html' : '/landing/:collectionHandle';
  const collectionRoutePath = !IS_SNAP ? '/landing/:collectionHandle' : '/landing/:collectionHandle.html';

  const pageRedirectPath = !IS_SNAP ? '/page/:pageHandle.html' : '/page/:pageHandle';
  const pageRoutePath = !IS_SNAP ? '/page/:pageHandle' : '/page/:pageHandle.html';

  return (
    <Router history={history}>
      <Suspense fallback={<LoadingScreen />}>
        <HelmetHelper title="Card.Gift" description="The ideal gift to give a friend or a loved one." />
        <div id="content">
          <MainNavigation />
          <ScrollToTop />
          <Switch>
            <Route path="/maintenance" exact component={MaintenancePageScreen} />
            {IS_MAINTENANCE_MODE && <Redirect from="*" to="/maintenance" exact />}
            {/* Start temporary redirects for TCN-860 */}
            <Route path="/landing/mastercard" exact>
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard-custom/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard-designed-love/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard-designed-dad/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard-designed-congratulations/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard-designed-christmas/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            <Route path="/product/mastercard-designed-birthday/:action?">
              <Redirect to="/product/digital-mastercard" />
            </Route>
            {/* End temporary redirects */}
            <SentryRoute path="/" exact component={HomeScreen} />
            <SentryRoute path="/audit/:auditType?" exact component={AuditScreen} />
            {!IS_SNAP && <Redirect from={productRedirectPath} to={productRoutePath} exact />}
            <SentryRoute path={productRoutePath} exact component={ProductDetailsScreen} />
            {IS_SNAP && <Redirect from={productRedirectPath} to={productRoutePath} exact />}
            {!IS_SNAP && <Redirect from={collectionRedirectPath} to={collectionRoutePath} exact />}
            <SentryRoute path={collectionRoutePath} exact component={DynamicCollectionScreen} />
            {IS_SNAP && <Redirect from={collectionRedirectPath} to={collectionRoutePath} exact />}
            {!IS_SNAP && <Redirect from={pageRedirectPath} to={pageRoutePath} exact />}
            <SentryRoute path={pageRoutePath} exact component={DefaultPageScreen} />
            {IS_SNAP && <Redirect from={pageRedirectPath} to={pageRoutePath} exact />}
            <Route path="/cards" exact>
              <Redirect to="/cards/all" />
            </Route>
            <SentryRoute path="/cards/:collection" exact component={CardFinderScreen} />
            <SentryRoute path="/product/:productHandle/:action" exact component={ProductDetailsScreen} />
            <Route path="/corporate">
              <Redirect to="/cards/all" />
            </Route>
            {/* <SentryRoute path="/corporate/product/:productHandle?/:action?" exact component={CorporateCollectionScreen} />
            <SentryRoute path="/corporate/:collectionHandle?/:productHandle?/:action?" exact component={CorporateCollectionScreen} /> */}
            <SentryRoute path="/page/:pageHandle/:action" exact component={DefaultPageScreen} />
            <SentryRoute path="/cart" exact component={CartScreen} />
            <SentryRoute path="/redeem" exact component={RedemptionLandingScreen} />
            <SentryRoute path="/redeem/product/:productHandle?" exact component={ProductFinderScreen} />
            <SentryRoute path="/redeem/product/:productHandle/:action?" exact component={ProductDetailsScreen} />
            <SentryRoute path="/redeem/:cardRef" exact component={RedemptionActivationScreen} />
            <SentryRoute path="/redeem/:cardRef/activated/:swapRef?" exact component={RedemptionPostActivationScreen} />
            <SentryRoute path="/redeem/collection/:collectionHandle" exact component={ProductFinderScreen} />
            <SentryRoute path="/redeem/:collectionHandle/:productHandle/:action?" exact component={ProductFinderScreen} />
            <SentryRoute path="/search" exact component={FullResultSearchScreen} />
            <Route path="/activate">
              <Redirect to="/redeem" />
            </Route>
            <Route path="/him" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-him-card" />
            </Route>
            <Route path="/her" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-her-card" />
            </Route>
            <Route path="/teen" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-teen-card" />
            </Route>
            <Route path="/kids" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-kids-card" />
            </Route>
            <Route path="/home" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-home-card" />
            </Route>
            <Route path="/active" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-active-card" />
            </Route>
            <Route path="/baby" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-baby-card" />
            </Route>
            <Route path="/shop" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-shop-card" />
            </Route>
            <Route path="/gift" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-gift-card" />
            </Route>
            <Route path="/love" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/the-love-card" />
            </Route>
            <Route path="/eftpos" exact>
              <ExternalRedirect url="https://www.eftpos.gift" />
            </Route>
            <Route path="/register" exact>
              <ExternalRedirect url="https://thecardnetwork.com.au/pages/register-multi-choice-cards" />
            </Route>
            <Route
              path="/cdn-off"
              exact
              render={() => {
                d('CDN Disabled...');
                localStorage.setItem('CDN_DISABLED', 'true');
                return <div>CDN has been disabled...</div>;
              }}
            />
            <Route
              path="/cdn-on"
              exact
              render={() => {
                d('CDN Enabled...');
                localStorage.removeItem('CDN_DISABLED');
                return <div>CDN has been enabled...</div>;
              }}
            />
            <SentryRoute>
              <NotFound />
            </SentryRoute>
          </Switch>
        </div>
        <Footer />
      </Suspense>
    </Router>
  );
};

export default CGRouter;
